import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import * as Menu from "@/components/ui/menu";
import { Text } from "@/components/ui/text";
import { api } from "@api";
import { AppDrawer, appDialogConfirm } from "@components";
import { DataGuard } from "@components/DataGuard";
import LayoutHeader from "@components/LayoutHeader";
import Panel from "@components/Panel";
import VideoPlayer from "@components/VideoPlayer";
import { A, useNavigate, useParams } from "@solidjs/router";
import { useAuth, userId } from "@state/auth";
import { useGoWatch } from "@state/goWatch/state";
import { useQuery } from "@tanstack/solid-query";
import dayjs from "dayjs";
import { ArrowLeft, Edit, EllipsisVertical, Trash2 } from "lucide-solid";
import { Show } from "solid-js";
import { Portal } from "solid-js/web";
import { useService } from "solid-services";
import { Box, Flex, Grid } from "styled-system/jsx";

import BubbleComments from "@features/Comments/Bubble";
import { TOPICS } from "@features/communityContent/topics";
import Create from "../List/Create";
import { useState } from "../List/state";
import Poster from "./Poster";
import { goWatchCommentsAdapter } from "./goWatchCommentsAdapter";

export default function SingleGoWatchVideo() {
  const params = useParams();
  const navigate = useNavigate();
  const { deleteVideo } = useService(useGoWatch)();
  const { editVideo, setEditVideo, isCreateFormOpen, setIsCreateFormOpen } = useService(useState)();
  const { isAdmin } = useService(useAuth)();

  const videoQuery = useQuery(() => ({
    queryKey: ['go-watch-video', params.id],
    queryFn: async () => {
      const { data, error } = await api()
        .from('go_watch_videos')
        .select(`
          *,
          author:profile_id (
            id,
            username,
            profile_private (role)
          )
        `)
        .eq('id', params.id)
        .single();

      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!params.id,
  }));

  return (
    <DataGuard
      query={videoQuery}
    >
      {(video) => (
        <>
          <LayoutHeader
            title={video?.title}
            subTitle={dayjs(video?.created_at).format('dddd, MMMM D, YYYY')}
            rightContent={
              <Flex gap='2' alignItems='center'>
                <A href={'/go-watch'}>
                  <Button size='xs' variant='outline'>
                    <ArrowLeft />
                    Back to Go Watch
                  </Button>
                </A>

                <Show when={isAdmin() || video?.profile_id === userId()}>
                  <Menu.Root
                    onSelect={(details) => {
                      if (details.value === 'edit') setEditVideo(video);
                      if (details.value === 'delete') {
                        appDialogConfirm(async () => {
                          await deleteVideo(video.id);
                          navigate('/go-watch');
                        }, 'Are you sure you want to delete this video?')();
                      }
                    }}
                  >
                    <Menu.Trigger
                      asChild={(triggerProps) => (
                        <IconButton variant='outline' size='xs' {...triggerProps()}>
                          <EllipsisVertical size={16} />
                        </IconButton>
                      )}
                    />
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content>
                          <Menu.Item minH={8} value="edit">
                            <Edit size={14} />
                            <Text textStyle="xs">Edit</Text>
                          </Menu.Item>
                          <Menu.Item minH={8} value="delete" color="error.10">
                            <Trash2 size={14} />
                            <Text textStyle="xs">Delete</Text>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                </Show>
              </Flex>
            }
          />

          <Panel height="calc(var(--vh) * 100 - 102px)" overflowY="auto">
            <Grid gap='0' width="full" maxW="1040px" py="24px" mx="auto" px={{ base: '22px', smDown: '16px' }}>
              <Box
                bg="black"
                borderRadius="14px"
                overflow="hidden"
                borderWidth='1px'
                borderStyle='solid'
                borderColor='var(--colors-editorial-hair)'
                aspectRatio="16/9"
              >
                <VideoPlayer provider={video?.provider} provider_id={video?.provider_id} />
              </Box>

              <Text
                as='h1'
                fontSize='23px'
                fontWeight='800'
                letterSpacing='-.02em'
                lineHeight='1.26'
                color='editorialInk'
                mt='20px'
                mb='14px'
              >
                {video?.title}
              </Text>

              <Poster
                username={video?.author?.username}
                id={video?.author?.id}
                rank={(video?.author?.profile_private as any)?.role}
                date={video?.created_at}
                topicLabel={video ? TOPICS[video.topic].label : ''}
                topicColor={video ? TOPICS[video.topic].color : '#000'}
                provider={video?.provider as any}
                providerId={video?.provider_id ?? ''}
              />

              <Show
                when={video?.description}
                fallback={
                  <Text fontSize='14px' color='editorialInk3' fontStyle='italic' py='18px' borderBottomWidth='1px' borderBottomStyle='solid' borderBottomColor='var(--colors-editorial-hair)'>
                    No description added.
                  </Text>
                }
              >
                <Text
                  fontSize='14px'
                  lineHeight='1.62'
                  color='editorialInk2'
                  py='18px'
                  borderBottomWidth='1px'
                  borderBottomStyle='solid'
                  borderBottomColor='var(--colors-editorial-hair)'
                  whiteSpace='pre-wrap'
                >
                  {video?.description}
                </Text>
              </Show>

              <Box mt='6px' borderTopWidth='1px' borderTopStyle='solid' borderTopColor='var(--colors-editorial-hair)' pt='18px'>
                <BubbleComments
                  entityId={video?.id}
                  adapter={goWatchCommentsAdapter}
                />
              </Box>
            </Grid>
          </Panel>

          <AppDrawer
            open={isCreateFormOpen()}
            onOpenChange={({ open }) => setIsCreateFormOpen(open)}
            title={editVideo() ? 'Edit Video' : 'Submit a video'}
            size='md'
          >
            <Create onSuccess={() => setIsCreateFormOpen(false)} />
          </AppDrawer>
        </>
      )}
    </DataGuard>
  );
}
